import React, { useCallback, useMemo } from 'react'
import PropTypes from 'prop-types'
import { useFormattingContext } from '../../../FormattingProvider/FormattingContext'
import { useFirmReportData } from '../../../../../api/coreData'
import { useDenaliFilter } from '../../filters/DenaliFilterContextProvider'
import ComponentTreeMap from '../../../../charts/echarts/ComponentTreeMap'

const useFormatters = (valueFormat) => {
  const { formatter } = useFormattingContext()
  const valueFormatter = useCallback(d => formatter(d, valueFormat), [formatter, valueFormat])
  return { valueFormatter }
}

function groupData (data, indexBy, valueAccessor, colorAccessor, parentValue = null) {
  const [currentKey, ...restKeys] = indexBy

  const groups = data.reduce((acc, item) => {
    const key = item[currentKey]
    if (!acc[key]) acc[key] = []
    acc[key].push(item)
    return acc
  }, {})

  const children = Object.keys(groups).map(key => {
    const items = groups[key]
    const colorValue = colorAccessor ? items[0][colorAccessor] : undefined

    if (restKeys.length === 0) {
      const value = Number(items[0][valueAccessor])
      return {
        name: key,
        value,
        parentValue,
        allocation: parentValue ? value / parentValue : null,
        ...(colorValue !== undefined && {
          itemStyle: {
            color: colorValue
          }
        })
      }
    }

    const childNodes = groupData(items, restKeys, valueAccessor, colorAccessor)
    const value = childNodes.reduce((acc, item) => acc + Number(item.value), 0)

    return {
      name: key,
      value,
      parentValue,
      allocation: parentValue ? value / parentValue : null,
      ...(colorValue !== undefined && {
        itemStyle: {
          color: colorValue
        }
      }),
      children: childNodes
    }
  })

  if (parentValue == null && indexBy.length === 1) {
    const total = children.reduce((sum, node) => sum + node.value, 0)
    return children.map(child => ({
      ...child,
      parentValue: total,
      allocation: total ? child.value / total : 0
    }))
  }

  return children
}

function DenaliComponentTreeMap ({
  reportId,
  baseQuery,
  indexBy,
  valueAccessor,
  valueFormat,
  colorAccessor,
  rootLabel,
  ...seriesProps
}) {
  const { filter, membershipFilter } = useDenaliFilter()
  const query = useMemo(
    () => ({
      ...baseQuery,
      membershipFilter,
      filters: { ...(filter || {}), ...(baseQuery?.filters || {}) },
      reportId
    }),
    [reportId, baseQuery, filter, membershipFilter]
  )

  const { valueFormatter } = useFormatters(valueFormat)
  const { data: report, isLoading } = useFirmReportData(query)

  const chartData = useMemo(() => {
    if (!report) return {}
    return groupData(report.data, indexBy, valueAccessor, colorAccessor)
  }, [report, indexBy, valueAccessor, colorAccessor])

  if (isLoading) return null

  return (
    <ComponentTreeMap
      data={chartData}
      valueFormat={valueFormatter}
      {...seriesProps}
    />
  )
}

DenaliComponentTreeMap.propTypes = {
  reportId: PropTypes.string,
  baseQuery: PropTypes.object,
  rootLabel: PropTypes.string,
  indexBy: PropTypes.array,
  valueAccessor: PropTypes.string,
  valueFormat: PropTypes.string,
  colorAccessor: PropTypes.string
}

DenaliComponentTreeMap.defaultProps = {
  valueFormat: 'marketValue'
}

export default DenaliComponentTreeMap
